/*
 * First-use Karl notice. The API refuses verdicts until the account accepts,
 * and the acceptance is stored per account, so a reader who already accepted
 * in the web editor never sees this again. A failed save keeps the notice open;
 * a caller without an account is sent to sign in instead of looping.
 */
import { Button, Text } from "@fluentui/react-components";
import { useTranslation } from "../../i18n/useTranslation";
import { buildPortalUrl } from "../common/portalLinks";
import { KarlAvatar } from "./KarlAvatar";

export type KarlConsentFailure = "save" | "account" | null;

export function KarlConsentModal({
  open,
  submitting,
  failure,
  onClose,
  onAccept,
  onGoToAccount,
}: {
  open: boolean;
  submitting: boolean;
  failure: KarlConsentFailure;
  onClose: () => void;
  onAccept: () => void;
  onGoToAccount: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  if (!open) return null;

  const accountRequired = failure === "account";

  return (
    <div className="karl-modal-overlay">
      <div
        className="karl-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="karl-consent-title"
        data-karl-consent-modal
      >
        <KarlAvatar compact animated={false} />
        <h2 id="karl-consent-title" className="karl-modal__title">
          {t.karlConsentTitle}
        </h2>
        <p className="karl-modal__copy">{t.karlConsentDisclaimer}</p>
        <p className="karl-modal__copy">{t.karlConsentPrivacyNote}</p>
        <a
          className="karl-modal__link"
          data-karl-consent-privacy-link
          href={buildPortalUrl("/privacy")}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.karlConsentPrivacyLink}
        </a>

        {failure ? (
          <Text role="alert" className="karl-modal__error" data-karl-consent-error>
            {accountRequired ? t.karlConsentAccountRequired : t.karlConsentFailed}
          </Text>
        ) : null}

        <div className="karl-modal__actions">
          <Button appearance="secondary" onClick={onClose} disabled={submitting}>
            {t.karlConsentCancel}
          </Button>
          {accountRequired ? (
            <Button appearance="primary" onClick={onGoToAccount} disabled={submitting}>
              {t.karlConsentGoToAccount}
            </Button>
          ) : (
            <Button appearance="primary" onClick={onAccept} disabled={submitting}>
              {t.karlConsentAccept}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
